import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, storage, auth } from '../../../firebase';
import '../../../styles/EditSection.scss';
import { IconButton, Button } from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Delete, Edit, Add } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import {Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText, TextField} from "@mui/material";

const EditSection = ({ sections, setSections }) => {
    
    const originalSections = sections;
    const [menuSections, setMenuSections] = useState(sections);
    const [hasChanges, setHasChanges] = useState(false);
    const [openInsertDialog, setOpenInsertDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [newSection, setNewSection] = useState({sectionName: "", order: originalSections.length+1, subtitle: "", deleted: false});

    const handleClear = () => {
        setNewSection({sectionName: "", order: menuSections.length+1, subtitle: "", deleted: false});
        setHasChanges(false);
    }

    const handleSave = () => {
        const updatedSections = menuSections
                                    .sort((a, b) => a.order - b.order)  // Ordina per il campo `order`
                                    .map((section, index) => ({         // Aggiorna il campo `order` in sequenza
                                        id: section.id,
                                        name: section.sectionName,
                                        sectionName: section.sectionName,
                                        subtitle: section.subtitle,
                                        deleted: !!section.deleted,
                                        items: section.items,
                                        order: index + 1,               // Assegna un ordine sequenziale a partire da 1
                                    }));
        setSections(updatedSections);
        setMenuSections(updatedSections.filter(s => !s.deleted));
        handleClear();
    }

    const handleNew = () => {
        handleOpenInsertDialog();
    }

    const handleEdit = (section) => {
        setNewSection(section);
        handleOpenInsertDialog();
    }

    const handleOpenInsertDialog = () => {
        setOpenInsertDialog(true);
    };

    const handleCloseInsertDialog = () => {
        setNewSection({sectionName: "", order: menuSections.length+1, subtitle: "", deleted: false});
        setOpenInsertDialog(false);
    };

    const handleOpenRefreshDialog = () => {
        setOpenCancelDialog(true);
    };

    const handleCloseRefreshDialog = () => {
        setOpenCancelDialog(false);
    };

    const shiftUp = (section) => {
        setMenuSections(prevSections => {
            const index = prevSections.findIndex(s => s.id === section.id);
            if (index <= 0) return prevSections;
    
            const newSections = [...prevSections];
            newSections[index].order -= 1;
            newSections[index - 1].order += 1;

            return newSections.sort((a, b) => a.order - b.order);
        });
        setHasChanges(true);
    };
    
    const shiftDown = (section) => {
        setMenuSections(prevSections => {
            const index = prevSections.findIndex(s => s.id === section.id);
            if (index === -1 || index === prevSections.length - 1) return prevSections;
    
            const newSections = [...prevSections];
            newSections[index].order += 1;
            newSections[index + 1].order -= 1;
    
            return newSections.sort((a, b) => a.order - b.order);
        });
        setHasChanges(true);
    };
    

    const deleteSection = (section) => {
        const deleted = !section.deleted;
        setMenuSections(menuSections.map(s => ({...s, deleted: s.id === section.id ? deleted : s.deleted})));
        setHasChanges(true);
    }

    const isBtnDownDisabled = (section) => {
        return section.order === menuSections.length;
    }

    const isBtnUpDisabled = (section) => {
        return section.order === 1;
    }

    return (
        <>
            <div className="main-cta-container">
                <Button onClick={handleNew} variant="contained" startIcon={<Add/>}>
                    Nuovo
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={!hasChanges} startIcon={<SaveIcon/>}>
                    Salva
                </Button>
                <Button onClick={handleOpenRefreshDialog} disabled={!hasChanges} variant="contained" >
                    <RefreshIcon/>
                </Button>
            </div>
            <div className="edit-sections-container">
                {menuSections.map(section => 
                    <React.Fragment key={section.id}>
                        <div className="order-btn-container">
                            <IconButton onClick={() => shiftUp(section)} variant="contained" disabled={isBtnUpDisabled(section)} className='order-btn'>
                                <ArrowDropUpIcon/>
                            </IconButton>
                            <IconButton onClick={() => shiftDown(section)} variant="contained" disabled={isBtnDownDisabled(section)} className='order-btn'>
                                <ArrowDropDownIcon/>
                            </IconButton>
                        </div>
                        <div className="section-details-container">
                            <div className={"section-name " + (section.deleted ? "--deleted" : "")}>{section.sectionName}</div>
                            <div className={"section-subtitle " + (section.deleted ? "--deleted" : "")}>{section.subtitle}</div>
                        </div>
                        <div className="edit-btn-container">
                            <IconButton onClick={() => handleEdit(section)} disabled={section.deleted} variant="contained" className='edit-btn'>
                                <Edit/>
                            </IconButton>
                            <IconButton onClick={() => deleteSection(section)} disabled={section.items?.length > 0} variant="contained" className='delete-btn'>
                                {section.deleted ? <ReplayIcon/> : <Delete/>}
                            </IconButton>
                        </div>
                    </React.Fragment>
                )}
            </div>


            {/* Insert / Edit dialog */}
            <Dialog
                open={openInsertDialog}
                onClose={handleCloseInsertDialog}
                PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                    event.preventDefault();
                    if (newSection.id) {
                        setMenuSections(menuSections.map(s => ({...s, 
                                                            sectionName: newSection.id === s.id ? newSection.sectionName : s.sectionName,
                                                            subtitle: newSection.id === s.id ? newSection.subtitle : s.subtitle
                                                        })));
                    } else {
                        setMenuSections([...menuSections, {...newSection, id: Date.now().toString()}]);
                    }
                    setHasChanges(true);
                    handleCloseInsertDialog();
                },
                }}
            >
                <DialogTitle>{newSection.id ? 'Aggiorna' : 'Aggiungi'} Sezione</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nome Sezione"
                        fullWidth
                        value={newSection.sectionName}
                        onChange={(e) => setNewSection({ ...newSection, sectionName: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        label="Descrizione"
                        fullWidth
                        value={newSection.subtitle}
                        onChange={(e) => setNewSection({ ...newSection, subtitle: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button type="submit" className='confirm-btn'>{newSection.id ? 'Aggiorna' : 'Aggiungi'}</Button>
                    <Button onClick={handleCloseInsertDialog} className='cancel-btn'>Annulla</Button>
                </DialogActions>
            </Dialog>

            {/* Refresh Dialog */}
            <Dialog
                open={openCancelDialog}
                onClose={handleCloseRefreshDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Sei sicuro di voler ricaricare? Le modifiche apportate verranno perse.
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                <Button onClick={() => {
                        handleClear(); 
                        setMenuSections(originalSections);
                        handleCloseRefreshDialog();
                    }} className='confirm-btn' autoFocus>
                    Conferma
                </Button>
                <Button onClick={handleCloseRefreshDialog} className='cancel-btn'>Annulla</Button>
                </DialogActions>
            </Dialog>

        </>
    );

}

export default EditSection;